import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getReport, listReports, markReportViewed } from "@/__generated__/api/me/me";
import type {
  ListReportsParams,
  ReportListItemOut,
  ReportOut,
  ReportOutContent,
  ReportOutPrivateSection,
  ReportViewedOut,
  ReportsOut,
} from "@/__generated__/api/wrenAPI.schemas";
import type { QueryShape } from "../meHooks";

export interface ReportListItem {
  uuid: string;
  weekStart: string;
  weekEnd: string;
  title: string;
  publishedAt: string;
  isNew: boolean;
}

export interface ReportCover {
  eyebrow: string;
  title: string;
  read: string;
}

export interface MessagesSection {
  type: "messages";
  heading: string;
  count: number;
  delta: string;
  spark: number[];
}

export interface BalanceTopic {
  label: string;
  count: number;
  tone: string;
}

export interface BalanceSection {
  type: "balance";
  heading: string;
  ratio: string;
  caption: string;
  positivePct: number;
  topics: BalanceTopic[];
}

export interface MomentSection {
  type: "moment";
  heading: string;
  quote: string;
  speaker: string;
  cite: string;
  caption: string;
}

export interface TrySection {
  type: "try";
  heading: string;
  instead: string;
  try: string;
  caption: string;
}

export type UnknownReportSection = {
  type: string;
  heading?: string;
  [key: string]: unknown;
};

export type ReportSection =
  | MessagesSection
  | BalanceSection
  | MomentSection
  | TrySection
  | UnknownReportSection;

export interface ReportSignoff {
  main: string;
  sub: string;
}

export interface ReportContent {
  version: number;
  cover: ReportCover;
  sections: ReportSection[];
  signoff: ReportSignoff;
}

export interface PrivateSection {
  heading: string;
  body: string[];
  ps: string;
}

export interface Report extends ReportListItem {
  viewedAt: string | null;
  content: ReportContent;
  privateSection: PrivateSection | null;
}

export interface ReportsResult {
  data: ReportListItem[] | undefined;
  total: number;
  hasMore: boolean;
  loadMore: () => void;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: Error | null;
}

interface ReportsCursor {
  beforeTs: string;
  beforeUuid: string;
}

export interface ViewedResult {
  viewedAt: string;
}

const REPORTS_QUERY_KEY = ["me", "reports"] as const;

function asObject(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function asString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function asNumber(value: unknown): number {
  return typeof value === "number" ? value : 0;
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
}

function reportListItemFromWire(row: ReportListItemOut): ReportListItem {
  return {
    uuid: row.uuid,
    weekStart: row.week_start,
    weekEnd: row.week_end,
    title: row.title,
    publishedAt: row.published_at,
    isNew: row.is_new,
  };
}

function reportSectionFromWire(raw: unknown): ReportSection {
  const section = asObject(raw);
  const type = asString(section.type);
  if (type === "messages") {
    return {
      type,
      heading: asString(section.heading),
      count: asNumber(section.count),
      delta: asString(section.delta),
      spark: Array.isArray(section.spark)
        ? section.spark.filter((point): point is number => typeof point === "number")
        : [],
    };
  }
  if (type === "balance") {
    return {
      type,
      heading: asString(section.heading),
      ratio: asString(section.ratio),
      caption: asString(section.caption),
      positivePct: asNumber(section.positive_pct),
      topics: Array.isArray(section.topics)
        ? section.topics.map((topic) => {
            const t = asObject(topic);
            return {
              label: asString(t.label),
              count: asNumber(t.count),
              tone: asString(t.tone),
            };
          })
        : [],
    };
  }
  if (type === "moment") {
    return {
      type,
      heading: asString(section.heading),
      quote: asString(section.quote),
      speaker: asString(section.speaker),
      cite: asString(section.cite),
      caption: asString(section.caption),
    };
  }
  if (type === "try") {
    return {
      type,
      heading: asString(section.heading),
      instead: asString(section.instead),
      try: asString(section.try),
      caption: asString(section.caption),
    };
  }
  return { ...section, type };
}

function reportContentFromWire(raw: ReportOutContent): ReportContent {
  const content = asObject(raw);
  const cover = asObject(content.cover);
  const signoff = asObject(content.signoff);
  return {
    version: asNumber(content.version),
    cover: {
      eyebrow: asString(cover.eyebrow),
      title: asString(cover.title),
      read: asString(cover.read),
    },
    sections: Array.isArray(content.sections)
      ? content.sections.map(reportSectionFromWire)
      : [],
    signoff: {
      main: asString(signoff.main),
      sub: asString(signoff.sub),
    },
  };
}

function privateSectionFromWire(raw: ReportOutPrivateSection): PrivateSection | null {
  if (!raw) return null;
  const section = asObject(raw);
  return {
    heading: asString(section.heading),
    body: asStringArray(section.body),
    ps: asString(section.ps),
  };
}

function reportFromWire(report: ReportOut): Report {
  return {
    ...reportListItemFromWire({ ...report, is_new: report.viewed_at == null }),
    viewedAt: report.viewed_at,
    content: reportContentFromWire(report.content),
    privateSection: privateSectionFromWire(report.private_section),
  };
}

export function reportQueryKey(reportUuid: string) {
  return [...REPORTS_QUERY_KEY, reportUuid] as const;
}

export function useReports(options: { pageSize?: number; enabled?: boolean } = {}): ReportsResult {
  const { pageSize = 20, enabled = true } = options;
  // Cursor-paged over (published_at, uuid), mirroring usePastSessions — a
  // single-page read made letters beyond the first page unreachable. The key
  // carries pageSize (v5 amendment pattern) so hub-sized and archive-sized
  // reads never share one cache entry.
  const q = useInfiniteQuery({
    queryKey: [...REPORTS_QUERY_KEY, pageSize] as const,
    enabled,
    initialPageParam: null as ReportsCursor | null,
    queryFn: async ({ pageParam }) => {
      const params: ListReportsParams = { limit: pageSize };
      if (pageParam) {
        params.before_ts = pageParam.beforeTs;
        params.before_uuid = pageParam.beforeUuid;
      }
      const res = await listReports(params);
      const data = res.data as ReportsOut;
      return {
        reports: data.reports.map(reportListItemFromWire),
        total: data.total,
        nextCursor:
          data.next_before_ts && data.next_before_uuid
            ? { beforeTs: data.next_before_ts, beforeUuid: data.next_before_uuid }
            : null,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });
  const pages = q.data?.pages;
  return {
    data: pages ? pages.flatMap((page) => page.reports) : undefined,
    total: pages?.[0]?.total ?? 0,
    hasMore: !!q.hasNextPage,
    loadMore: () => {
      if (q.hasNextPage && !q.isFetchingNextPage) {
        void q.fetchNextPage();
      }
    },
    isLoading: q.isLoading,
    isLoadingMore: q.isFetchingNextPage,
    error: q.error instanceof Error ? q.error : null,
  };
}

export function useReport(reportUuid: string): QueryShape<Report> {
  const q = useQuery({
    queryKey: reportQueryKey(reportUuid),
    enabled: reportUuid.length > 0,
    queryFn: async () => {
      const res = await getReport(reportUuid);
      return reportFromWire(res.data as ReportOut);
    },
  });
  return {
    data: q.data,
    isLoading: q.isLoading,
    error: q.error instanceof Error ? q.error : null,
  };
}

export function useMarkReportViewed(reportUuid: string) {
  const queryClient = useQueryClient();
  return useMutation<ViewedResult, Error, void>({
    mutationFn: async () => {
      const res = await markReportViewed(reportUuid);
      const data = res.data as ReportViewedOut;
      return { viewedAt: data.viewed_at };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: REPORTS_QUERY_KEY });
    },
  });
}
